/**
 * Hacia dónde va cada cambio de pantalla.
 *
 * The animation itself is CSS (`styles.css`, «Pantallas»), run by the browser's view transitions:
 * a picture of the old screen and one of the new, never the live page. This decides what it
 * should say, and says it on `<html>` before the pictures are taken:
 *
 *   · `data-transicion` — `adelante` for deeper into the app, `atras` for coming back. Coming
 *     back is going to the screen before this one, or up a level; everything else is forward.
 *     Where you are in the app is a place, and the motion is what makes it feel like one.
 *   · `data-morph` — when the thing tapped was one of the big doors on the home screen, it
 *     becomes the page, and on the way back the page becomes it again.
 *   · `data-pestanas` — moving along the tabs of a section. The tabs stay put and the screen
 *     under them moves towards the side the new tab is on.
 *
 * All three stay until the next change of screen. They only style the transition's own pictures, and
 * the names they hand out only apply while a transition is running (`:active-view-transition`
 * in styles.css) — so there is nothing to clean up on a timer, which a slow phone would outrun,
 * flipping the direction halfway through.
 *
 * Browsers without view transitions get none of this, and the page fades in as it always did.
 */

import type { AnyRouter } from '@tanstack/react-router';
import { pestanaDe } from './secciones.js';

export type Origen = 'principal' | 'vendido';

export const conTransiciones =
  typeof document !== 'undefined' && typeof document.startViewTransition === 'function';

const reducido = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

let tocado: Origen | undefined;

/** Called by a big door on the home screen as it is tapped: that element becomes the page. */
export function marcarOrigen(origen: Origen): void {
  tocado = origen;
}

/**
 * Whether a screen that is mounting is arriving by a view transition. A page that is will not
 * fade itself in as well — the transition already brings it — and one that is not (the first
 * screen, or a browser without them) fades as it always did.
 */
export function llegaConTransicion(): boolean {
  try {
    return document.documentElement.matches(':active-view-transition');
  } catch {
    // Older than the selector (it came with transition types): the attribute is close enough.
    return Boolean(document.documentElement.dataset.transicion);
  }
}

/**
 * For `createRouter`'s view transitions. A change of address that stays on the same screen —
 * only the search — is not a change of screen, and somebody who asked for less motion gets none.
 */
export function tiposDeTransicion({ pathChanged }: { pathChanged: boolean }): string[] | false {
  return pathChanged && !reducido() ? ['pantalla'] : false;
}

const profundidad = (ruta: string) => ruta.split('/').filter(Boolean).length;

/** A screen behind this one, and the door it was entered by. */
export interface Rastro {
  href: string;
  origen?: Origen;
}

type Lugar = { href: string; pathname: string };

/**
 * Which way a change of screen goes, and through which door. Updates the trail it is given and
 * touches nothing else, so the tests can walk it.
 *
 * Along the tabs of a section is sideways: towards the tab's side, and in the same place in the
 * trail, so that leaving the section is still going back the way it was entered, door and all.
 * Back is a screen further down the trail (usually the one before this, but a form saved into a
 * detail leaves two screens to come back through at once), or a level up without having come
 * that way, which starts the trail again from there. Everything else is forward.
 */
export function decidir(
  pila: Rastro[],
  desde: Lugar,
  hacia: Lugar,
  origen: Origen | undefined,
): { direccion: 'adelante' | 'atras'; morph?: Origen; pestanas?: true } {
  if (pila.length === 0) pila.push({ href: desde.href });

  const de = pestanaDe(desde.pathname);
  const a = pestanaDe(hacia.pathname);
  if (de && a && de.seccion === a.seccion) {
    const puerta = pila.pop()?.origen;
    pila.push(puerta ? { href: hacia.href, origen: puerta } : { href: hacia.href });
    return { direccion: a.indice > de.indice ? 'adelante' : 'atras', pestanas: true };
  }

  for (let i = pila.length - 2; i >= 0; i--) {
    if (pila[i]!.href !== hacia.href) continue;
    // Out through the door the screen above this one was entered by.
    const morph = pila[i + 1]?.origen;
    pila.length = i + 1;
    return { direccion: 'atras', morph };
  }
  if (profundidad(hacia.pathname) < profundidad(desde.pathname)) {
    pila.length = 0;
    pila.push({ href: hacia.href });
    return { direccion: 'atras' };
  }
  pila.push({ href: hacia.href, origen });
  // A shift of going forward all day should not keep every screen of it.
  if (pila.length > 50) pila.shift();
  return { direccion: 'adelante', morph: origen };
}

const pila: Rastro[] = [];

/**
 * A transition cut short is nothing to report. A change of screen that starts while the last one
 * is still sliding in (back pressed mid-slide, the window resized, a screen that changes its own
 * address as it arrives) cuts it short, and the browser rejects that transition's `ready`:
 * «AbortError: Transition was skipped», «InvalidStateError: Transition was aborted because of
 * invalid state». The router starts each transition and keeps only `updateCallbackDone`, so nobody
 * listened, and each landed on the console as an unhandled rejection. The browser checks fail on any
 * page error, and two deploys in a row stopped on one in a different script each (2026-10-14). The new
 * screen is there either way. `ready` and `finished` are listened to here, for every transition;
 * `updateCallbackDone`, which carries a screen that failed to draw, still goes to the router.
 */
function escucharTransiciones(): void {
  const iniciar = document.startViewTransition.bind(document);
  document.startViewTransition = ((como?: Parameters<typeof iniciar>[0]) => {
    const transicion = iniciar(como);
    transicion.ready.catch(() => {});
    transicion.finished.catch(() => {});
    return transicion;
  }) as typeof document.startViewTransition;
}

export function seguirTransiciones(router: AnyRouter): void {
  if (!conTransiciones) return;
  escucharTransiciones();
  const html = document.documentElement;
  const limpiar = () => {
    delete html.dataset.transicion;
    delete html.dataset.morph;
    delete html.dataset.pestanas;
  };

  router.subscribe('onBeforeNavigate', ({ fromLocation, toLocation, pathChanged }) => {
    const origen = tocado;
    tocado = undefined;
    if (!fromLocation || !pathChanged) return limpiar();

    const { direccion, morph, pestanas } = decidir(pila, fromLocation, toLocation, origen);
    html.dataset.transicion = direccion;
    if (morph) html.dataset.morph = morph;
    else delete html.dataset.morph;
    if (pestanas) html.dataset.pestanas = '';
    else delete html.dataset.pestanas;
  });
}
